import * as React from "react"

/**
 * Whether the application's left navigation is on screen, and how to flip it.
 *
 * Exposed as context rather than threaded through the router because the
 * control that hides the navigation does not live beside it: a reviewer asks
 * for the chrome to go while reading a document, so the button belongs in that
 * document's own header, several routes below the shell that owns the state.
 *
 * Reading this outside an `AppShell` yields a hidden-is-false value and a
 * toggle that does nothing, so a screen rendered on its own (a test, a
 * storybook page) does not have to provide one.
 */
export interface NavVisibility {
  hidden: boolean
  setHidden: (hidden: boolean) => void
}

const NavVisibilityContext = React.createContext<NavVisibility>({
  hidden: false,
  setHidden: () => undefined,
})

export function NavVisibilityProvider({
  value,
  children,
}: {
  value: NavVisibility
  children: React.ReactNode
}): JSX.Element {
  return <NavVisibilityContext.Provider value={value}>{children}</NavVisibilityContext.Provider>
}

export function useNavVisibility(): NavVisibility {
  return React.useContext(NavVisibilityContext)
}
